import { useState } from 'react';
import {
  BarChart3,
  Radio,
  Zap,
  AlertTriangle,
  RefreshCw,
  Brain,
  Clock,
} from 'lucide-react';
import TelemetryExplorer from '../components/dashboard/TelemetryExplorer';
import AgentPerformanceMetrics from '../components/dashboard/AgentPerformanceMetrics';
import ExceptionDashboard from '../components/dashboard/ExceptionDashboard';
import WorkflowVisualization from '../components/dashboard/WorkflowVisualization';
import LearningInsights from '../components/dashboard/LearningInsights';
import TemporalUIEmbed from '../components/dashboard/TemporalUIEmbed';

type DashboardView = 'overview' | 'telemetry' | 'performance' | 'exceptions' | 'workflows' | 'learning' | 'temporal';

const TABS: { id: DashboardView; label: string; icon: typeof BarChart3 }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'telemetry', label: 'Telemetry', icon: Radio },
  { id: 'performance', label: 'Performance', icon: Zap },
  { id: 'exceptions', label: 'Exceptions', icon: AlertTriangle },
  { id: 'workflows', label: 'Workflows', icon: RefreshCw },
  { id: 'learning', label: 'Learning', icon: Brain },
  { id: 'temporal', label: 'Temporal UI', icon: Clock },
];

export default function Dashboard() {
  const [activeView, setActiveView] = useState<DashboardView>('overview');

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Unified Dashboard
              </h1>
              <p className="text-sm text-neutral-500 mt-1">
                Monitoring, Telemetry &amp; Analytics
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-50 border border-accent-200 rounded-lg">
              <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse" />
              <span className="text-sm text-accent-700 font-medium">Live</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="container mx-auto px-6">
          <nav className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap
                    ${
                      activeView === tab.id
                        ? 'border-coral-500 text-coral-600 bg-coral-50'
                        : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                    }
                  `}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        {activeView === 'overview' && <DashboardOverview />}
        {activeView === 'telemetry' && <TelemetryExplorer />}
        {activeView === 'performance' && <AgentPerformanceMetrics />}
        {activeView === 'exceptions' && <ExceptionDashboard />}
        {activeView === 'workflows' && <WorkflowVisualization />}
        {activeView === 'learning' && <LearningInsights />}
        {activeView === 'temporal' && <TemporalUIEmbed />}
      </main>
    </div>
  );
}

// Overview component that shows all views in a grid
function DashboardOverview() {
  return (
    <div className="space-y-6">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Active Workflows" value="12" change="+3" trend="up" />
        <StatCard title="Events/min" value="847" change="+12%" trend="up" />
        <StatCard title="Active Exceptions" value="3" change="-2" trend="down" />
        <StatCard title="Learning Models" value="5" change="0" trend="neutral" />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PreviewCard title="Recent Telemetry">
          <TelemetryExplorer compact />
        </PreviewCard>
        <PreviewCard title="Agent Performance">
          <AgentPerformanceMetrics compact />
        </PreviewCard>
        <PreviewCard title="Active Exceptions">
          <ExceptionDashboard compact />
        </PreviewCard>
        <PreviewCard title="Active Workflows">
          <WorkflowVisualization compact />
        </PreviewCard>
      </div>

      {/* Full Width Learning Insights */}
      <PreviewCard title="Learning Insights">
        <div className="h-80">
          <LearningInsights compact />
        </div>
      </PreviewCard>
    </div>
  );
}

function PreviewCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-warm-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <span className="text-xs text-coral-600 cursor-pointer hover:text-coral-500">View All</span>
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
}

function StatCard({ title, value, change, trend }: StatCardProps) {
  const trendColors = {
    up: 'text-accent-600',
    down: 'text-red-500',
    neutral: 'text-neutral-400',
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-5 border-l-[3px] border-l-coral-500 shadow-warm-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{title}</span>
        <span className={`text-xs font-medium ${trendColors[trend]}`}>{change}</span>
      </div>
      <div className="text-xl font-bold font-mono tabular-nums text-neutral-900">{value}</div>
    </div>
  );
}
